import { useState } from "react";
import "./App.scss";
import { createTheme, ThemeProvider } from "@mui/material/styles";
// Please note we are using react router 6 - google searches tend to return v5 docs
import { Routes, Route, Outlet, Navigate, matchPath, useLocation } from "react-router-dom";

import MasterLayout from "ui/organisms/MasterLayout";
import {
  HeaderContainer,
  HeaderHamburgerMenu,
  HeaderLogo,
  HeaderProfileNav,
} from "@thrivetribe/mvf.ui.organisms.header";
import { Footer } from "ui/organisms/Footer";
import { profileNavLinks, faqLinks } from "configs/UrlConfig";
import { SidebarContainer, SidebarLinks, SidebarFaq, SidebarSocialMedia } from "ui/organisms/Sidebar";
import { AuthProvider, AuthService, Logout } from "./components/auth/index";
import { AuthConfig } from "configs/AuthConfig";
import { FitnessAndFootball } from "pages/FitnessAndFootball/FitnessAndFootballPage";
import { FoodAndNutritionPage } from "pages/FoodAndNutrition/FoodAndNutritionPage";
import { UsefulExtras } from "pages/UsefulExtras/UsefulExtras";
import { PlayerSupport } from "pages/PlayerSupport/PlayerSupport";
import { ArticlesPage } from "pages/Articles/ArticlesPage";
import { Homepage } from "pages/Homepage/Homepage";
import { MyClubPage } from "pages/MyClub";
import { ArticlePage } from "pages/Article/ArticlePage";
import RequireAuth from "components/auth/requireAuth";
import EmergencyContact from "Onboarding/EmergencyContact/EmergencyContact";
import Allergies from "Onboarding/Allergies/Allergies";
import SpecifyAllergies from "Onboarding/Allergies/SpecifyAllergies";
import Medication from "Onboarding/Medications/Medication";
import SpecifyMedication from "Onboarding/Medications/SpecifyMedication";
import EthnicitySelection from "Onboarding/Ethnicity/EthnicitySelection";
import AgeChecker from "Onboarding/AgeChecker/AgeChecker";
import PortalComplete from "Onboarding/PortalComplete/PortalComplete";
import { ForgotPasswordPage } from "pages/ForgottenPassword/ForgottenPasswordPage";
import AlmostDone from "Onboarding/AlmostDone/AlmostDone";
import { ForgottenPasswordRequested } from "pages/ForgottenPassword/ForgottenPasswordRequested";
import { RequestInvitePage } from "pages/InviteRequest/RequestInvite";
import { InviteRequestedPage } from "pages/InviteRequest/InviteRequested";
import { RecipesPage } from "pages/Recipes/RecipesPage";
import { RecipePage } from "pages/Recipe/RecipePage";
import { UserProvider } from "components/user/UserProvider";
import { Login } from "components/auth/Login";
import { useGetUserQuery } from "redux/userApiSlice";
import { LoadingSpinner } from "@thrivetribe/mvf.ui.molecules.loading-spinner";
import AnyHealthConditions from "Onboarding/HealthConditions/AnyHealthConditions";
import SpecifyHealthConditions from "Onboarding/HealthConditions/SpecifyHealthConditions";
import Page404Error from "pages/Errors/Page404Error";
import RtkErrorMessage from "ui/organisms/Errors/RtkErrorMessage";
import NoClubErrorPage from "pages/Errors/NoClubErrorPage";
import { GA_TAG_ID, Gtag } from "configs/ReactGa";
import { ForgottenPasswordExpired } from "pages/ForgottenPassword/ForgottenPasswordExpired";
import { ForgotPasswordResent } from "pages/ForgottenPassword/ForgotPasswordResent";

const theme = createTheme({
  spacing: 10,
  breakpoints: {
    values: {
      xs: 0,
      sm: 0,
      md: 718,
      lg: 1180,
      xl: 1180,
    },
  },
});

const authService = new AuthService({
  clientId: AuthConfig.client_id,
  location: window.location,
  provider: AuthConfig.authority,
  redirectUri: AuthConfig.redirect_uri,
  logoutRedirectUri: AuthConfig.logout_redirect_uri,
  scopes: AuthConfig.scope,
  policy: AuthConfig.policy,
});

function App() {
  let [isSidebarOpen, setIsSitebarOpen] = useState<boolean>(true);
  let authObj = authService.getAuthUser();
  let { data: userObj, isLoading, isError, error } = useGetUserQuery(authObj?.sub ?? "");
  let isValid = userObj?.userStatus === "valid";
  const { pathname } = useLocation();
  const isAuthPath = matchPath("/authentication/*", pathname);

  let userDisplayName = (authObj?.given_name ?? "").concat(" ", authObj?.family_name ?? "").trim(); 

  if (isLoading || userObj === null) {
    return <LoadingSpinner fullscreen={true} />;
  }

  if (authObj?.sub) {
    Gtag("config", GA_TAG_ID, {
      user_id: authObj.sub,
    });
  } else {
    Gtag("config", GA_TAG_ID);
  }

  let setupHeader = (
    <AuthProvider authService={authService}>
      <HeaderContainer>
        <HeaderLogo />
        <HeaderProfileNav links={profileNavLinks} user={userDisplayName} />
      </HeaderContainer>
    </AuthProvider>
  );

  let mvfHeader = (
    <AuthProvider authService={authService}>
      <HeaderContainer>
        <HeaderHamburgerMenu isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSitebarOpen} />
        <HeaderLogo />
        <HeaderProfileNav links={profileNavLinks} user={userDisplayName} />
      </HeaderContainer>
    </AuthProvider>
  );

  return (
    <AuthProvider authService={authService}>
      <UserProvider user={userObj} authObj={authObj}>
        <ThemeProvider theme={theme}>
          <Routes>
            <Route
              path="/"
              element={
                isValid ? (
                  <MasterLayout
                    header={mvfHeader}
                    footer={<Footer />}
                    sidebar={
                      <SidebarContainer isSidebarOpen={isSidebarOpen}>
                            <SidebarLinks                                   
                          isSidebarOpen={isSidebarOpen}
                          setIsSitebarOpen={setIsSitebarOpen}
                        />
                        <SidebarSocialMedia />
                        <SidebarFaq faqHeading="Got questions?" faqLinks={faqLinks} />
                      </SidebarContainer>
                    }
                    body={
                      <RequireAuth>
                        {isError ? <RtkErrorMessage error={error} renderNav={true} /> : <Outlet />}
                      </RequireAuth>
                    }
                  />
                ) : authService.isAuthenticated() ? (
                  <Navigate replace to="/portal-setup" />
                ) : (
                  <Login />
                )
              }
            >
              <Route path="/" element={<Homepage />} />
              <Route path="my-club" element={<MyClubPage />} />
              <Route path="fitness-and-football" element={<FitnessAndFootball />} />
              <Route path="food-and-nutrition" element={<FoodAndNutritionPage />} />
              <Route path="useful-extras" element={<UsefulExtras />} />
              <Route path="player-support" element={<PlayerSupport />} />
              <Route path="articles" element={<Outlet />}>
                <Route index element={<ArticlesPage />} />
                <Route path=":slug" element={<ArticlePage />}></Route>
              </Route>
              <Route path="article-list" element={<Outlet />}>
                <Route index element={<ArticlesPage />} />
                <Route
                  path="pre-season"
                  element={
                    <ArticlesPage
                      heading="MAN v FAT Basics Articles"
                      tagType="journeyTags"
                      tags={["PreSeason", "FirstSeason"]}
                    />
                  }
                ></Route>
                <Route path=":campaign" element={<ArticlesPage />}></Route>
              </Route>
              <Route path="recipes" element={<Outlet />}>
                <Route index element={<RecipesPage />} />
                <Route path=":slug" element={<RecipePage />}></Route>
              </Route>
              <Route path="/error/no-club-found" element={<NoClubErrorPage />} />
            </Route>
            <Route path="log-out" element={<Logout />} />
            <Route path="logged-out" element={<Login />}></Route>

            <Route
              path="/portal-setup"
              element={
                isError ? (
                  <MasterLayout header={setupHeader} body={<RtkErrorMessage error={error} renderNav={false} />} />
                ) : !isValid ? (
                  <RequireAuth>
                    <Outlet />
                  </RequireAuth>
                ) : (
                  <Navigate replace to="/" />
                )
              }
            >
              <Route index element={<AlmostDone />} />
              <Route path="age" element={<AgeChecker />} />
              <Route path="ethnicity" element={<EthnicitySelection />} />
              <Route path="emergency-contact" element={<EmergencyContact />} />
              <Route path="any-health-conditions" element={<AnyHealthConditions />} />
              <Route path="health-conditions" element={<SpecifyHealthConditions />} />
              <Route path="any-allergies" element={<Allergies />} />
              <Route path="specify-allergies" element={<SpecifyAllergies />} />
              <Route path="any-medication" element={<Medication />} />
              <Route path="specify-medication" element={<SpecifyMedication />} />
              <Route path="complete" element={<PortalComplete />} />
            </Route>
            <Route path="forgotten-password" element={<ForgotPasswordPage />} />
            <Route path="password-requested" element={<ForgottenPasswordRequested />} />
            <Route path="reset-password" element={<ForgotPasswordResent /> } />
            <Route path="request-invite" element={<RequestInvitePage />} />
            <Route path="invite-requested" element={<InviteRequestedPage />} />
            <Route path="expired" element={<ForgottenPasswordExpired />} />
            {isAuthPath === null && (
              <>
                <Route path="*" element={<Page404Error />}></Route>
              </>
            )}
          </Routes>
        </ThemeProvider>
      </UserProvider>
    </AuthProvider>
  );
}

export default App;
